import type { WorkflowEdge, WorkflowNode } from "@ease/db/schema";

export interface WorkflowGalleryTemplate {
  templateId: string;
  name: string;
  description: string;
  category: "generic" | "service" | "ecommerce" | "agency" | "referral";
  vertical: "all" | "service" | "ecommerce" | "agency";
  triggerType: string;
  reentryPolicy: "once_ever" | "after_completion" | "allow_multiple";
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

function node(id: string, type: string, x: number, y: number, config: Record<string, unknown>): WorkflowNode {
  return { id, type, position: { x, y }, config };
}

function edge(id: string, source: string, target: string, condition?: string): WorkflowEdge {
  return { id, source, target, condition };
}

export const WORKFLOW_GALLERY_TEMPLATES: readonly WorkflowGalleryTemplate[] = [
  {
    templateId: "generic-welcome-series",
    name: "Welcome Series",
    description: "Send a welcome email immediately, wait one day, then create a follow-up task.",
    category: "generic",
    vertical: "all",
    triggerType: "trigger_form",
    reentryPolicy: "once_ever",
    nodes: [
      node("trigger", "trigger_form", 120, 120, { label: "Form Submitted", formId: "contact-form" }),
      node("email", "action_send_email", 360, 120, { label: "Send Welcome Email", templateId: "welcome-series-01" }),
      node("wait", "condition_wait", 600, 120, { label: "Wait 1 day", durationHours: 24 }),
      node("task", "action_create_task", 840, 120, { label: "Create Follow-up Task", taskTitle: "Call new lead" }),
    ],
    edges: [edge("e1", "trigger", "email"), edge("e2", "email", "wait"), edge("e3", "wait", "task")],
  },
  {
    templateId: "generic-reengagement",
    name: "Re-Engagement",
    description: "Revive dormant contacts with a win-back email and a tag-based branch.",
    category: "generic",
    vertical: "all",
    triggerType: "trigger_event",
    reentryPolicy: "after_completion",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Inactive 30 Days", eventType: "contact.inactive_30d" }),
      node("email", "action_send_email", 360, 120, { label: "Send Win-Back Email", templateId: "reengagement-01" }),
      node("if", "condition_if", 600, 120, { label: "Clicked offer?", field: "lastCampaign.clicked", operator: "equals", value: "true" }),
      node("tag", "action_add_tag", 840, 60, { label: "Tag Re-engaged", tag: "re-engaged" }),
      node("task", "action_create_task", 840, 180, { label: "Create Rescue Task", taskTitle: "Review dormant contact" }),
    ],
    edges: [edge("e1", "trigger", "email"), edge("e2", "email", "if"), edge("e3", "if", "tag", "true"), edge("e4", "if", "task", "false")],
  },
  {
    templateId: "generic-review-request",
    name: "Review Request",
    description: "Ask for a review after service completion and follow up by SMS.",
    category: "generic",
    vertical: "service",
    triggerType: "trigger_event",
    reentryPolicy: "once_ever",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Service Completed", eventType: "service.completed" }),
      node("wait", "condition_wait", 360, 120, { label: "Wait 4 hours", durationHours: 4 }),
      node("email", "action_send_email", 600, 120, { label: "Send Review Email", templateId: "review-request-01" }),
      node("sms", "action_send_sms", 840, 120, { label: "Send Review SMS", message: "How did we do? Leave a quick review: {{review.link}}" }),
    ],
    edges: [edge("e1", "trigger", "wait"), edge("e2", "wait", "email"), edge("e3", "email", "sms")],
  },
  {
    templateId: "generic-birthday-offer",
    name: "Birthday Offer",
    description: "Send a birthday email and add a VIP nurture tag.",
    category: "generic",
    vertical: "all",
    triggerType: "trigger_scheduled",
    reentryPolicy: "after_completion",
    nodes: [
      node("trigger", "trigger_scheduled", 120, 120, { label: "Daily Birthday Check", cron: "0 9 * * *" }),
      node("if", "condition_if", 360, 120, { label: "Birthday Today?", field: "contact.birthdayToday", operator: "equals", value: "true" }),
      node("email", "action_send_email", 600, 80, { label: "Send Birthday Offer", templateId: "birthday-offer-01" }),
      node("tag", "action_add_tag", 600, 180, { label: "Add VIP Tag", tag: "birthday-offer-sent" }),
    ],
    edges: [edge("e1", "trigger", "if"), edge("e2", "if", "email", "true"), edge("e3", "if", "tag", "true")],
  },
  {
    templateId: "service-appointment-reminder",
    name: "Appointment Reminder",
    description: "Send an email reminder one day before and an SMS reminder two hours before.",
    category: "service",
    vertical: "service",
    triggerType: "trigger_event",
    reentryPolicy: "allow_multiple",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Appointment Booked", eventType: "booking.confirmed" }),
      node("wait-day", "condition_wait", 360, 120, { label: "Wait until 24h before", durationHours: 24 }),
      node("email", "action_send_email", 600, 120, { label: "Email Reminder", templateId: "appointment-reminder-email" }),
      node("wait-hours", "condition_wait", 840, 120, { label: "Wait 22 hours", durationHours: 22 }),
      node("sms", "action_send_sms", 1080, 120, { label: "SMS Reminder", message: "Reminder: your appointment starts soon. Need to reschedule? {{booking.manageUrl}}" }),
    ],
    edges: [edge("e1", "trigger", "wait-day"), edge("e2", "wait-day", "email"), edge("e3", "email", "wait-hours"), edge("e4", "wait-hours", "sms")],
  },
  {
    templateId: "service-no-show-recovery",
    name: "No-Show Recovery",
    description: "Recover missed appointments with a fast SMS and an internal task.",
    category: "service",
    vertical: "service",
    triggerType: "trigger_event",
    reentryPolicy: "after_completion",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "No Show", eventType: "booking.no_show" }),
      node("sms", "action_send_sms", 360, 120, { label: "SMS Rebook Prompt", message: "We missed you today. Rebook here: {{booking.rebookUrl}}" }),
      node("task", "action_create_task", 600, 120, { label: "Follow-up Task", taskTitle: "Check in after no-show" }),
    ],
    edges: [edge("e1", "trigger", "sms"), edge("e2", "sms", "task")],
  },
  {
    templateId: "service-feedback-loop",
    name: "Feedback Loop",
    description: "Collect feedback, branch promoters, and escalate detractors.",
    category: "service",
    vertical: "service",
    triggerType: "trigger_event",
    reentryPolicy: "after_completion",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Service Completed", eventType: "service.completed" }),
      node("email", "action_send_email", 360, 120, { label: "Send Feedback Survey", templateId: "feedback-loop-01" }),
      node("if", "condition_if", 600, 120, { label: "Promoter?", field: "survey.nps", operator: "equals", value: "9+" }),
      node("tag", "action_add_tag", 840, 60, { label: "Promoter Tag", tag: "nps-promoter" }),
      node("task", "action_create_task", 840, 180, { label: "Recovery Task", taskTitle: "Investigate low feedback score" }),
    ],
    edges: [edge("e1", "trigger", "email"), edge("e2", "email", "if"), edge("e3", "if", "tag", "true"), edge("e4", "if", "task", "false")],
  },
  {
    templateId: "ecommerce-cart-recovery",
    name: "Abandoned Cart Recovery",
    description: "Recover abandoned carts with email then SMS if the cart is still open.",
    category: "ecommerce",
    vertical: "ecommerce",
    triggerType: "trigger_event",
    reentryPolicy: "after_completion",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Cart Abandoned", eventType: "cart.abandoned" }),
      node("wait", "condition_wait", 360, 120, { label: "Wait 1 hour", durationHours: 1 }),
      node("email", "action_send_email", 600, 120, { label: "Recovery Email", templateId: "cart-recovery-01" }),
      node("if", "condition_if", 840, 120, { label: "Still open?", field: "cart.completed", operator: "equals", value: "false" }),
      node("sms", "action_send_sms", 1080, 120, { label: "SMS Recovery", message: "Your cart is still waiting: {{cart.checkoutUrl}}" }),
    ],
    edges: [edge("e1", "trigger", "wait"), edge("e2", "wait", "email"), edge("e3", "email", "if"), edge("e4", "if", "sms", "true")],
  },
  {
    templateId: "ecommerce-win-back",
    name: "Lapsed Buyer Win-Back",
    description: "Re-engage past buyers after 60 days without a purchase.",
    category: "ecommerce",
    vertical: "ecommerce",
    triggerType: "trigger_event",
    reentryPolicy: "after_completion",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "No Purchase 60d", eventType: "customer.inactive_60d" }),
      node("email", "action_send_email", 360, 120, { label: "Win-Back Email", templateId: "win-back-01" }),
      node("tag", "action_add_tag", 600, 120, { label: "Mark Win-Back", tag: "win-back-sent" }),
    ],
    edges: [edge("e1", "trigger", "email"), edge("e2", "email", "tag")],
  },
  {
    templateId: "agency-client-onboarding",
    name: "Client Onboarding",
    description: "Kick off agency onboarding with welcome email, task creation, and data collection.",
    category: "agency",
    vertical: "agency",
    triggerType: "trigger_manual",
    reentryPolicy: "once_ever",
    nodes: [
      node("trigger", "trigger_manual", 120, 120, { label: "Manual Kickoff" }),
      node("email", "action_send_email", 360, 120, { label: "Welcome Email", templateId: "agency-onboarding-01" }),
      node("task", "action_create_task", 600, 120, { label: "Create Intake Task", taskTitle: "Collect onboarding assets" }),
      node("crm", "action_update_crm", 840, 120, { label: "Set Lifecycle Stage", field: "lifecycleStage", value: "onboarding" }),
    ],
    edges: [edge("e1", "trigger", "email"), edge("e2", "email", "task"), edge("e3", "task", "crm")],
  },
  {
    templateId: "referral-post-purchase",
    name: "Referral Follow-Up",
    description: "Ask happy customers for referrals over a 30-day post-purchase sequence.",
    category: "referral",
    vertical: "service",
    triggerType: "trigger_event",
    reentryPolicy: "once_ever",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Booking Completed", eventType: "service.booking.completed" }),
      node("wait-3", "condition_wait", 360, 120, { label: "Wait 3 days", durationHours: 72 }),
      node("sms", "action_send_sms", 600, 120, { label: "Referral SMS", message: "Loved your visit? Share your referral link: {{referralLink}}" }),
      node("wait-14", "condition_wait", 840, 120, { label: "Wait 11 more days", durationHours: 264 }),
      node("email", "action_send_email", 1080, 120, { label: "Referral Email", templateId: "referral-incentive-01" }),
    ],
    edges: [edge("e1", "trigger", "wait-3"), edge("e2", "wait-3", "sms"), edge("e3", "sms", "wait-14"), edge("e4", "wait-14", "email")],
  },
  {
    // SD-D: closes SD-C Gap G3 (review-ask seeded template missing). Seeds a
    // default review-ask workflow per vertical. Subscribes to the wire event
    // emitted by `booking-service.complete()`, waits the 3-day signal delay
    // declared by `review-collection-engine.SIGNAL_DELAYS.booking_completed`,
    // then runs `reputation.send_review_request@v1` with the
    // `booking_completed` signal. Suppression rules (cooldown, open support,
    // recent marketing, low NPS, churn risk) are enforced inside the
    // reputation handler.
    templateId: "service-review-ask-default",
    name: "Review Ask — Service",
    description:
      "Default review-ask for service tenants: wait 3 days after booking completion, then send a review request through the reputation engine.",
    category: "service",
    vertical: "service",
    triggerType: "trigger_event",
    reentryPolicy: "once_ever",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Booking Completed", eventType: "service.booking.completed" }),
      node("wait", "condition_wait", 360, 120, { label: "Wait 3 days", durationHours: 72 }),
      node("review", "action_send_review_request", 600, 120, {
        label: "Send Review Request",
        actionRef: "reputation.send_review_request@v1",
        signal: "booking_completed",
      }),
    ],
    edges: [edge("e1", "trigger", "wait"), edge("e2", "wait", "review")],
  },
  {
    templateId: "ecommerce-review-ask-default",
    name: "Review Ask — Ecommerce",
    description:
      "Default review-ask for ecommerce tenants: wait 7 days after the order ships, then send a review request through the reputation engine.",
    category: "ecommerce",
    vertical: "ecommerce",
    triggerType: "trigger_event",
    reentryPolicy: "once_ever",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Order Delivered", eventType: "order.delivered" }),
      node("wait", "condition_wait", 360, 120, { label: "Wait 7 days", durationHours: 168 }),
      node("review", "action_send_review_request", 600, 120, {
        label: "Send Review Request",
        actionRef: "reputation.send_review_request@v1",
        signal: "purchase_delivered",
      }),
    ],
    edges: [edge("e1", "trigger", "wait"), edge("e2", "wait", "review")],
  },
  {
    templateId: "agency-review-ask-default",
    name: "Review Ask — Agency",
    description:
      "Default review-ask for agency tenants: trigger when a project milestone is signed off, then send a review request through the reputation engine.",
    category: "agency",
    vertical: "agency",
    triggerType: "trigger_event",
    reentryPolicy: "once_ever",
    nodes: [
      node("trigger", "trigger_event", 120, 120, { label: "Milestone Signed Off", eventType: "agency.milestone.completed" }),
      node("wait", "condition_wait", 360, 120, { label: "Wait 1 day", durationHours: 24 }),
      node("review", "action_send_review_request", 600, 120, {
        label: "Send Review Request",
        actionRef: "reputation.send_review_request@v1",
        signal: "milestone_completed",
      }),
    ],
    edges: [edge("e1", "trigger", "wait"), edge("e2", "wait", "review")],
  },
] as const;

export function listWorkflowGalleryTemplates(): readonly WorkflowGalleryTemplate[] {
  return WORKFLOW_GALLERY_TEMPLATES;
}

export function getWorkflowGalleryTemplate(templateId: string): WorkflowGalleryTemplate | undefined {
  return WORKFLOW_GALLERY_TEMPLATES.find((template) => template.templateId === templateId);
}
